import type { AgentApiResult, AgentPrepared, AgentToolDef } from '#shared/agent/tool-defs';
import { NAV_PAGES } from '#shared/agent/tool-defs';
import { consumeApproval, type Approval } from '#shared/agent/confirm-gate';
import { agentRequestHeaders, pickExtraBody } from '#shared/agent/request-headers';
import { useSessionStore } from '~/composables/stores/session';
import { globalFetchOptions, toastError } from './helpers';

const NAV_PAGE_PATHS: Record<(typeof NAV_PAGES)[number], string> = {
  dashboard: '/',
  applicants: '/applicants',
  cases: '/applications',
  appointments: '/appointments',
  funds: '/funds',
  'funding-sources': '/funding-sources',
  'service-areas': '/service-areas',
  reports: '/reports',
  maps: '/maps',
  schedule: '/schedule',
};

/**
 * Tools executed entirely on the client (no API request). Same-tab SPA
 * navigation keeps the chat window and conversation alive.
 */
const CLIENT_TOOL_HANDLERS: Record<string, (input: any) => Promise<unknown>> = {
  navigate_to: async (input) => {
    let url: string | null = null;
    switch (input.target) {
      case 'applicant':
        url = input.id ? `/applicants/${input.id}/show` : null;
        break;
      case 'application':
        url = input.id ? `/applications/${input.id}/show` : null;
        break;
      case 'case':
        url = input.applicationId
          ? `/applications/${input.applicationId}/show${input.id ? `?arId=${input.id}` : ''}`
          : null;
        break;
      case 'page':
        url = input.page ? (NAV_PAGE_PATHS[input.page as keyof typeof NAV_PAGE_PATHS] ?? null) : null;
        break;
    }
    if (!url) {
      return {
        error: `Missing parameters for target "${input.target}" — applicant/application need id, case needs applicationId, page needs page.`,
      };
    }
    await navigateTo(url);
    return { navigated: true, url };
  },
};

/**
 * Executes an agent tool call against the Admin API with the logged-in user's JWT.
 * The return value is always a JSON-serializable tool output — failures are returned
 * as { error } so the model can recover, never thrown.
 */
export interface ExecuteOptions {
  /** Issued by the confirm gate when the user clicked the card's button; required for confirm-before-write tools. */
  approval?: Approval;
  /** Keys typed by the user on the confirm card (filtered by the tool's extraBodyKeys). */
  extraBody?: Record<string, unknown>;
}

const apiResult = (error: any): AgentApiResult => {
  const status = Number(error?.response?.status ?? error?.statusCode ?? 0);
  const data = error?.response?._data;
  return {
    ok: false,
    status,
    message:
      data?.error?.message ??
      data?.error?.Message ??
      data?.message ??
      error?.message ??
      'Please try again or contact support.',
    data: data?.data ?? null,
  };
};

/**
 * Read-only API access for a tool's `prepare` step (runs before the confirm card opens).
 * Same auth and assistant tagging as a tool call; it never carries X-Confirmed-Send.
 */
export const prepareAgentTool = async (def: AgentToolDef, input: unknown): Promise<AgentPrepared> => {
  if (!def.prepare) return {};
  const parsed = def.inputSchema.safeParse(input);
  if (!parsed.success) return { error: 'Invalid tool input.' };
  const call = async (method: 'GET' | 'POST', path: string, body?: unknown): Promise<AgentApiResult> => {
    const fetchOptions = globalFetchOptions();
    try {
      const raw = await $fetch<any>(path, {
        ...fetchOptions,
        method,
        body,
        headers: { ...fetchOptions.headers, ...agentRequestHeaders(def) },
      });
      return {
        ok: raw?.success !== false,
        status: 200,
        message: raw?.error?.message ?? raw?.message ?? '',
        data: raw?.data ?? raw?.Data ?? null,
      };
    } catch (error: any) {
      const result = apiResult(error);
      if (result.status === 401) useSessionStore().timeout();
      return result;
    }
  };
  try {
    return await def.prepare(parsed.data, {
      get: (path) => call('GET', path),
      preview: (path, body) => call('POST', path, body),
    });
  } catch (error: any) {
    return { error: error?.message ?? 'Could not prepare this action.' };
  }
};

export const executeAgentTool = async (
  def: AgentToolDef,
  input: unknown,
  options: ExecuteOptions = {}
): Promise<unknown> => {
  // A confirm-before-write tool runs only with a single-use Approval the gate issued from the user's click.
  const confirmed = def.requiresConfirmation ? consumeApproval(options.approval, def) : false;
  if (def.requiresConfirmation && !confirmed) {
    return { error: 'This action needs the user to confirm it first. Ask them to confirm; do not retry on your own.' };
  }

  // Re-validate the LLM-provided input; never trust it blindly
  const parsed = def.inputSchema.safeParse(input);
  if (!parsed.success) {
    const details = parsed.error.issues
      .map((issue) => `${issue.path.join('.') || 'input'}: ${issue.message}`)
      .join('; ');
    return { error: `Invalid tool input — ${details}` };
  }

  const clientHandler = CLIENT_TOOL_HANDLERS[def.name];
  if (clientHandler) {
    try {
      return await clientHandler(parsed.data);
    } catch (error: any) {
      return { error: error?.message ?? 'Client action failed.' };
    }
  }

  try {
    if (!def.buildRequest) return { error: `Tool ${def.name} has no request builder or client handler.` };
    const { path, query, body: builtBody } = def.buildRequest(parsed.data);
    const extra = pickExtraBody(def, options.extraBody);
    const body = Object.keys(extra).length ? { ...builtBody, ...extra } : builtBody;
    // Same auth header as globalFetchOptions; legacy tools just target the legacy base URL
    const fetchOptions = globalFetchOptions();
    if (def.api === 'legacy') {
      fetchOptions.baseURL = useRuntimeConfig().public.legacyApiBase;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- envelope shape varies per endpoint
    const raw = await $fetch<any>(path, {
      ...fetchOptions,
      method: def.method,
      query,
      body,
      headers: {
        ...fetchOptions.headers,
        // Marks SAMMI-initiated calls: skips the global loading overlay (plugins/6.global-loading.client.ts),
        // tags request logs and audit rows, and makes the API hold back any email/SMS unless this
        // request is the one the user confirmed (X-Confirmed-Send).
        ...agentRequestHeaders(def, { confirmed, runId: options.approval?.toolCallId }),
      },
    });

    // Unwrap the Admin API envelope: { success, data, ... } (some endpoints use PascalCase)
    const success = raw?.success ?? raw?.Success;
    if (success === false) {
      const message = raw?.error?.message ?? raw?.error?.Message ?? raw?.message;
      if (message == null) {
        // Some endpoints (e.g. GET /service-areas/{zip}/get-details) return
        // 200 { success: false, data: null } with no error as a normal
        // negative answer — report "not found" to the model, no error toast.
        return { found: false };
      }
      toastError('Error', message, 5000);
      return { error: message };
    }

    // selectResult expects the unwrapped payload, not the envelope
    const payload =
      raw && typeof raw === 'object' && ('data' in raw || 'Data' in raw) ? (raw.data ?? raw.Data) : raw;
    return def.selectResult ? def.selectResult(payload) : payload;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ofetch error shape
  } catch (error: any) {
    const status = error?.response?.status ?? error?.statusCode;
    if (status === 401) {
      // The global vue-query 401 handler doesn't cover raw $fetch — mirror it here
      useSessionStore().timeout();
      return { error: 'Session expired. The user has been signed out.' };
    }
    const message =
      error?.response?._data?.error?.message ??
      error?.response?._data?.error?.Message ??
      error?.message ??
      'Please try again or contact support.';
    toastError('Error', message, 5000);
    return { error: message };
  }
};
